import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { BookOpen, Image, FileText, User, HelpCircle, ExternalLink } from 'lucide-react';

const MemberDashboard = () => {
  const { user } = useAuth();

  const quickLinks = [
    { to: '/members/learning', icon: BookOpen, title: 'Learning', description: 'Access learning materials and resources' },
    { to: '/members/collections', icon: Image, title: 'Collections', description: 'View lodge artefacts and collections' },
    { to: '/members/minutes', icon: FileText, title: 'Meeting Minutes', description: 'Read and comment on meeting minutes' },
    { to: '/members/profile', icon: User, title: 'Profile', description: 'View and update your profile' },
    { to: '/members/support', icon: HelpCircle, title: 'Support', description: 'Contact the lodge administration' },
  ];

  return (
    <div className="p-6 md:p-12" data-testid="member-dashboard">
      <div className="max-w-4xl">
        {/* Welcome Section */}
        <section className="mb-10" data-testid="welcome-section">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Welcome, {user?.firstName}
          </h1>
          <p className="text-lg text-gray-600">
            Access your member resources and lodge information below.
          </p>
        </section>

        {/* Quick Links */}
        <section className="mb-10" data-testid="quick-links-section">
          <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4">Quick Links</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {quickLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link key={link.to} to={link.to} data-testid={`quick-link-${link.to.split('/').pop()}`}>
                  <Card className="border-gray-100 hover:shadow-md hover:border-emerald-200 transition-all h-full">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <Icon className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">{link.title}</h3>
                          <p className="text-sm text-gray-500 mt-1">{link.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>

        {/* WebCollect Link */}
        <section className="mb-10" data-testid="webcollect-section">
          <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <ExternalLink className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-gray-900">
                      Membership Login - WebCollect
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Access event bookings, subscriptions, and dues payments
                    </p>
                  </div>
                </div>
                <a
                  href="https://webcollect.org.uk/stdavid"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-medium transition-colors flex items-center gap-2"
                  data-testid="webcollect-link"
                >
                  Open WebCollect
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Membership Status */}
        <section data-testid="membership-status-section">
          <Card className="border-gray-100">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Membership Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Member Name</p>
                  <p className="font-medium text-gray-900" data-testid="status-name">
                    {user?.firstName} {user?.lastName}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Email</p>
                  <p className="font-medium text-gray-900" data-testid="status-email">
                    {user?.email}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Dues Status</p>
                  <span
                    className={`inline-flex px-2 py-1 rounded text-sm font-medium ${
                      user?.duesStatus === 'Yes'
                        ? 'bg-emerald-100 text-emerald-700'
                        : user?.duesStatus === 'No'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                    data-testid="status-dues"
                  >
                    {user?.duesStatus || 'Unknown'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default MemberDashboard;
